<template>
  <div class="sender">
    <div class="picture">
      <q-file
        v-model="files"
        outlined
        :label="$t('appCore.apps.components.sender.sendLabel')"
        multiple
        max-total-size="1073741824"
        max-files="20"
        accept=".jpg, image/*, video/*"
        class="input-file"
        @rejected="onRejected"
      />
    </div>
    <div class="send">
      <button
        class="button btn-bold"
        :class="{ 'btn-active': sending }"
        :disabled="stateStore.isAllButtonsDisabled || sending"
        @click="sendPicture"
      >
        <b>{{ btnText }}</b>
        <b v-if="sending"
          >{{ senderStore.sendedFiles }}
          {{ $t('appCore.apps.components.sender.from') }}
          {{ senderStore.filesToSend }}</b
        >
      </button>
    </div>
  </div>
</template>

<script>
import { useSenderStore } from '@/stores';
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import Tr from '@/i18n/translation';
import { useEventStore } from '@/stores/event/event.store';
import { useStateStore } from '@/stores/state.store';

export default {
  name: 'SenderWidget',
  props: {
    sendingText: {
      type: String,
      required: true,
    },
    sendText: {
      type: String,
      required: true,
    },
  },
  setup() {
    const eventStore = useEventStore();
    const senderStore = useSenderStore();
    const stateStore = useStateStore();
    const quasar = useQuasar();

    return {
      eventStore,
      senderStore,
      stateStore,
      quasar,
      onRejected(_rejectedEntries) {
        quasar.notify({
          type: 'negative',
          message: Tr.t('appCore.apps.event.sender.errors.wrongFiles'),
        });
      },
    };
  },
  data() {
    return {
      mainColor: this.eventStore.getColors.mainColor,
      buttonColor: this.eventStore.getColors.buttonColor,
      backgroundColor: this.eventStore.getColors.backgroundColor,
      files: ref(null),
    };
  },
  computed: {
    sending() {
      return this.senderStore.filesToSend !== 0;
    },
    btnText() {
      if (this.sending) {
        return this.sendingText;
      }

      return this.sendText;
    },
    btnColor() {
      return this.buttonColor ? this.buttonColor : this.mainColor;
    },
    btnColorRunning() {
      return this.btnColor + '00';
    },
    backgroundColorRunning() {
      return this.backgroundColor + 'EE';
    },
  },
  methods: {
    async sendPicture() {
      if (this.files) {
        this.files.forEach((file) => {
          this.senderStore.addImage(file, 'gallery', []);
        });
        this.files = null;
        const res = await this.senderStore.sendImages(
          'createSenderStream',
          'sendGalleryImages',
        );

        if (res) {
          this.quasar.notify({
            type: 'info',
            message: Tr.t('appCore.apps.event.sender.infos.sendedFiles'),
          });
        } else {
          this.quasar.notify({
            type: 'negative',
            message: Tr.t('appCore.apps.event.sender.errors.default'),
          });
        }
      } else {
        this.quasar.notify({
          type: 'negative',
          message: Tr.t('appCore.apps.event.sender.errors.notFiles'),
        });
      }
    },
  },
};
</script>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.q-field__input,
.q-file__filler,
.q-field__label,
.q-field--outlined,
.q-field__control,
.q-field__control::after {
  color: v-bind(mainColor);
}

.q-field__control::before {
  border: 1px solid v-bind(buttonColor) !important;
}
/* stylelint-enable selector-class-pattern */
</style>

<style lang="scss" scoped>
.sender {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 80%;
  margin: auto;
  align-items: stretch;
  text-transform: uppercase;
  letter-spacing: 2px;

  .picture {
    display: flex;
    flex-flow: row wrap;
    gap: 15px;
    justify-content: space-between;
    align-items: stretch;

    .input-file {
      width: 100%;
    }
  }

  .send {
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-items: center;
    gap: 15px;
  }

  .sender-text {
    font-size: 16px;
    border-bottom: 1px solid v-bind(btnColor);
    text-align: left;
    padding-bottom: 10px;
    white-space: nowrap;
    display: flex;
    align-items: end;
    flex-basis: auto;
  }

  .button {
    border-radius: 48.707px;
    border: 2px solid v-bind(btnColor);
    color: v-bind(btnColor);
    padding: 15px 25px;
    font-size: 25px;
    white-space: nowrap;
    text-align: center;
    transition: background-color 1s ease;

    &:hover {
      background-color: v-bind(btnColor);
      color: v-bind(backgroundColor);
    }

    @media screen and (max-width: 500px) {
      font-size: 18px;
      line-height: 30px;
      padding: 10px 20px;
    }
  }

  .btn-active {
    background-color: v-bind(btnColor);
    color: v-bind(backgroundColor);
    position: relative;
    overflow: hidden;
  }

  .btn-active::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      v-bind(btnColorRunning),
      v-bind(backgroundColorRunning)
    );
    border-radius: 0 26px 26px 0;
    animation: fill-loader 2s linear infinite;
  }

  @keyframes fill-loader {
    0% {
      left: -100%;
    }

    100% {
      left: 100%;
    }
  }

  .btn-bold {
    border: 3px solid v-bind(btnColor);
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
</style>
